import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { ProjectService } from '../services/project.service';
import { Note } from '../models/note';
import { image } from './image.const';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { environment } from '../../environments/environment';
import {
  NOTE_IMAGE_SAS_ROUTE,
  NotePhotoFailure,
  NotePhotoState,
  NoteKeyEncoder,
  classifyNoteImage,
  extractSasUrl,
  isNoteImageKey,
  sasFailureForStatus,
} from './note-image';
import { closeSubscription, runTeardown } from '../services/safe-teardown';

@Component({
  selector: 'app-screen3',
  templateUrl: './screen3.component.html',
  styleUrls: ['./screen3.component.scss'],
})
export class Screen3Component implements OnInit, OnDestroy {
  errorMessage: string = '';
  noteCollection: Note[] = [];
  imageSource: any;
  selectedImage: any = '';
  showImagePopup: boolean = false;
  showNewNoteInput: boolean = false;
  newNoteText: string = '';
  isSavingNote: boolean = false;

  /**
   * Per-key photo state, keyed by the note's `IM` blob key.
   *
   * RESOLVED ONCE, NOT PER RENDER. The template reads this map through plain
   * lookups, because a binding is re-evaluated on every change-detection pass —
   * putting the SAS request behind one would fire an HTTP call per cycle, on a
   * page that already polls. So the fetch happens when notes ARRIVE.
   *
   * A key with no entry has not been requested yet. A failure is stored as
   * 'unavailable' and SHOWN — see NotePhotoFailure.
   */
  private notePhotoStates = new Map<string, NotePhotoState>();

  /** Sanitized URLs for 'ready' keys, so the template gets a stable object per key. */
  private noteImageUrls = new Map<string, SafeResourceUrl>();

  /**
   * Keys whose image already failed to load once and were re-resolved. A SAS
   * lives one hour; a tab left open longer re-renders with an expired URL, so the
   * FIRST load failure fetches a fresh SAS. A second failure is final.
   */
  private noteImageReResolved = new Set<string>();

  selectedProjectSubscription?: Subscription;
  notesDataSubscription?: Subscription;

  constructor(
    private projectService: ProjectService,
    private sanitizer: DomSanitizer,
    private http: HttpClient
  ) {}

  ngOnInit() {
    this.projectService.isHelpOverlayVisible = false;

    this.createNotes();

    this.imageSource = this.sanitizer.bypassSecurityTrustResourceUrl(
      `data:image/png;base64, ${image}`
    );
  }

  /** Teardown must never abort a route deactivation — see safe-teardown.ts. */
  ngOnDestroy(): void {
    runTeardown('Screen3Component.ngOnDestroy', () => {
      closeSubscription('selectedProjectSubscription', this.selectedProjectSubscription);
      closeSubscription('notesDataSubscription', this.notesDataSubscription);
    });
  }

  createNotes() {
    // Data Subscriptions --------------------------------------------------------
    this.selectedProjectSubscription =
      this.projectService.selectedProject.subscribe(
        (data) => {
          this.noteCollection = [];
        },
        (error) => (this.errorMessage = error)
      );

    this.notesDataSubscription = this.projectService.notesData.subscribe(
      (data) => {
        if (data == null) {
          console.log('Error in Notes Comp: Notes data is null.');
          return;
        }

        if (data.length > 0) {
          this.noteCollection = data;
          this.resolveNoteImages(data);
        } else {
          console.log('Notes Screen > No Notes');
        }
      }
    );
  }

  /**
   * The value to bind to a note's <img [src]>.
   *
   * Pure lookup — no HTTP, safe to call from a template.
   *
   *   none    -> the bundled placeholder, byte-for-byte today's behaviour, because
   *              a note with genuinely no photo must look exactly as it does now
   *   inline  -> the existing base64 path, unchanged
   *   key     -> the resolved SAS URL once it has arrived, the placeholder while
   *              it is loading. An UNAVAILABLE key renders no <img> at all (the
   *              template shows the marker instead), so this is never asked.
   */
  noteImageSrc(note: Note): any {
    const kind = classifyNoteImage(note?.IM);
    if (kind === 'key') {
      const resolved = this.noteImageUrls.get((note.IM as string).trim());
      return resolved ?? this.base64Sanitize(null);
    }
    return this.base64Sanitize(note?.IM ?? null);
  }

  /** True once a key-backed note has a URL — the popup should not open without one. */
  noteImageReady(note: Note): boolean {
    return classifyNoteImage(note?.IM) !== 'key'
      || this.noteImageUrls.has((note.IM as string).trim());
  }

  /** The reason a key-backed photo cannot be shown, or null. Pure lookup. */
  notePhotoFailure(note: Note): NotePhotoFailure | null {
    if (classifyNoteImage(note?.IM) !== 'key') return null;
    const state = this.notePhotoStates.get((note.IM as string).trim());
    return state?.status === 'unavailable' ? state.reason : null;
  }

  /** Clicking a photo: open the popup only when there is a real image to show. */
  onNoteImageClick(note: Note): void {
    if (!this.noteImageReady(note)) return;
    this.showPopup(this.noteImageSrc(note));
  }

  /**
   * The <img> itself failed. For a key that is a missing blob (DS mints a SAS for
   * an absent blob, storage answers 404) or an expired SAS. Re-resolve ONCE for
   * the expiry case; a second failure is shown as unavailable.
   */
  onNoteImageError(note: Note): void {
    if (classifyNoteImage(note?.IM) !== 'key') return;
    const key = (note.IM as string).trim();
    if (this.notePhotoStates.get(key)?.status !== 'ready') return;
    if (!this.noteImageReResolved.has(key)) {
      this.noteImageReResolved.add(key);
      console.warn(`Notes: photo failed to load, fetching a fresh link once (key ${key})`);
      this.noteImageUrls.delete(key);
      this.requestSas(key);
      return;
    }
    this.markUnavailable(key, 'load-failed');
  }

  /**
   * Fetch a short-lived SAS URL for every note whose IM is a blob key.
   *
   * One request per DISTINCT key per project-load, never per render. A failure
   * does NOT blank the Notes tab — the note's text still renders — but it is
   * SHOWN on that note and logged with console.error, never swallowed into the
   * placeholder a photo-less note uses.
   */
  private resolveNoteImages(notes: Note[]): void {
    for (const note of notes) {
      if (!isNoteImageKey(note?.IM)) continue;
      const key = (note.IM as string).trim();
      if (this.notePhotoStates.has(key)) continue;
      this.requestSas(key);
    }
  }

  private requestSas(key: string): void {
    this.notePhotoStates.set(key, { status: 'loading' });
    // IM is passed VERBATIM; it already carries device/project — see NOTE_IMAGE_SAS_ROUTE.
    const params = new HttpParams({ encoder: new NoteKeyEncoder() }).set('key', key);

    this.http
      .get(`${environment.apiUrl}${NOTE_IMAGE_SAS_ROUTE}`, {
        params,
        responseType: 'text',
      })
      .subscribe({
        next: (body) => {
          const url = extractSasUrl(body);
          if (!url) {
            this.markUnavailable(key, 'bad-response');
            return;
          }
          this.notePhotoStates.set(key, { status: 'ready', url });
          this.noteImageUrls.set(key, this.sanitizer.bypassSecurityTrustResourceUrl(url));
        },
        error: (err: HttpErrorResponse) => {
          this.markUnavailable(key, sasFailureForStatus(err?.status ?? 0), err?.status);
        },
      });
  }

  private markUnavailable(key: string, reason: NotePhotoFailure, status?: number): void {
    this.notePhotoStates.set(key, { status: 'unavailable', reason });
    this.noteImageUrls.delete(key);
    console.error(
      `Notes: photo unavailable (${reason}${status != null ? `, HTTP ${status}` : ''}) for key ${key}`
    );
  }

  base64Sanitize(imageText: string | null): any {
    if (!imageText || imageText.length == 0) {
      return this.sanitizer.bypassSecurityTrustResourceUrl(
        `data:image/png;base64, ${image}`
      );
    } else {
      return this.sanitizer.bypassSecurityTrustResourceUrl(
        `data:image/png;base64, ${imageText}`
      );
    }
  }

  showPopup(imageText: string) {
    this.selectedImage = imageText;
    this.showImagePopup = true;
  }

  closePopup() {
    this.showImagePopup = false;
  }

  addNewNote() {
    // Show the text input field
    this.showNewNoteInput = true;
    this.newNoteText = '';
    this.isSavingNote = false;
  }

  onEnterSaveNote(event: Event) {
    // Prevent default Enter key behavior and stop propagation
    event.preventDefault();
    event.stopPropagation();
    
    // Save the note
    this.saveNewNote();
  }

  onBlurSaveNote() {
    // Only save on blur if we're not already saving
    // This prevents double-save when Enter triggers both keydown and blur
    if (!this.isSavingNote) {
      this.saveNewNote();
    }
  }

  saveNewNote() {
    // Prevent multiple simultaneous saves
    if (this.isSavingNote) {
      console.log('Already saving note, skipping duplicate save...');
      return;
    }

    // Validate that user entered some text
    if (!this.newNoteText || this.newNoteText.trim() === '') {
      this.showNewNoteInput = false;
      this.isSavingNote = false;
      return;
    }

    // Set flag to prevent multiple saves
    this.isSavingNote = true;
    console.log('Starting to save note...');

    // Get the current project name
    const projectName = this.projectService.getProject();
    
    if (!projectName) {
      alert('No project selected. Please select a project first.');
      this.showNewNoteInput = false;
      this.isSavingNote = false;
      return;
    }

    // Create a new note with current timestamp
    const newNote: Note = {
      IM: null,
      TX: this.newNoteText.trim(),
      T: new Date()
    };

    // Add the new note to the existing collection
    const allNotes = [...this.noteCollection, newNote];

    // Prepare the API URL
    const apiUrl = `${environment.apiUrl}v1/projects/notes`;
    const params = new HttpParams().set('projectName', projectName);

    // Call the API with all notes
    console.log('Sending notes to API:', apiUrl);
    console.log('Project Name:', projectName);
    console.log('Payload:', allNotes);

    this.http.post(apiUrl, allNotes, { params }).subscribe(
      (response) => {
        console.log('Notes saved successfully:', response);
        // Update the notes data in the service to reflect the changes
        this.projectService.setNotesData(allNotes);
        // Reset the input
        this.showNewNoteInput = false;
        this.newNoteText = '';
        this.isSavingNote = false;
        console.log('Save complete, flag reset');
      },
      (error) => {
        console.error('Error saving notes:', error);
        alert('Failed to save note. Please try again.');
        this.showNewNoteInput = false;
        this.isSavingNote = false;
      }
    );
  }

  cancelNewNote() {
    this.showNewNoteInput = false;
    this.newNoteText = '';
    this.isSavingNote = false;
  }
}
